import { renderNavbar } from '../components/navbar.js';
import { supabase, getCurrentUser, isSupabaseReady } from '../services/supabase.js';
import { escapeHtml } from '../utils/html.js';
import { renderAvatarInner } from '../utils/avatar.js';
import { buildLeaderboard, collectLeaderboardUserIds, mergeProfilesById, rankLabel } from '../utils/leaderboards.js';
import { buildPlatformShareUrl, buildScoreSharePayload, listSocialPlatforms } from '../utils/social-share.js';
import { generateScoreShareImage } from '../utils/social-image.js';
import { tr } from '../services/i18n.js';

export async function renderLeaderboards(container) {
  const params = new URLSearchParams((window.location.hash.split('?')[1] || ''));
  const scope = params.get('scope') || 'global';
  const competitionId = params.get('competition') || '';

  container.innerHTML = `
    <div class="page p-16" style="padding-bottom:100px;">
      <div class="app-header">
        <div class="brand">
          <div class="brand-mark">FV</div>
          <div>
            <p class="brand-title">${tr('Classements', 'Rankings')}</p>
            <p class="brand-subtitle">${tr('Global, amis et compétitions', 'Global, friends, and competitions')}</p>
          </div>
        </div>
        <a class="btn-secondary" href="#/quiz">Quiz</a>
      </div>
      <div id="leaderboards-content">
        <div class="glass-card skeleton" style="height:220px;"></div>
      </div>
    </div>
  `;
  renderNavbar(container);

  const content = container.querySelector('#leaderboards-content');
  if (!isSupabaseReady()) {
    content.innerHTML = `<div class="glass-card"><p class="text-muted">${tr('Supabase non configuré.', 'Supabase is not configured.')}</p></div>`;
    return;
  }

  try {
    const user = await getCurrentUser();
    const [profileSeed, debriefs, comments, reactions, competitions, friendships, quizEntries] = await Promise.all([
      safeQuery(() => supabase.from('profiles').select('id,pseudo,avatar_url,avatar_emoji').limit(200)),
      safeQuery(() => supabase.from('debriefs').select('id,user_id,competition_id').limit(1000)),
      safeQuery(() => supabase.from('comments').select('user_id,debrief_id').limit(2000)),
      safeQuery(() => supabase.from('reactions').select('user_id,debrief_id').limit(4000)),
      safeQuery(() => supabase.from('competitions').select('id,name').order('starts_at', { ascending: true })),
      user ? safeQuery(() => supabase.from('friendships').select('requester_id,addressee_id,status').eq('status', 'accepted')) : Promise.resolve([]),
      safeQuery(() => supabase.from('quiz_entries').select('user_id,challenge:match_challenges(competition_id)').limit(3000))
    ]);

    const friendIds = new Set([user?.id].filter(Boolean));
    friendships.forEach((f) => {
      if (f.requester_id === user?.id) friendIds.add(f.addressee_id);
      if (f.addressee_id === user?.id) friendIds.add(f.requester_id);
    });

    const normalizedQuizEntries = quizEntries.map((entry) => ({
      user_id: entry.user_id,
      competition_id: entry.challenge?.competition_id || null
    }));
    const activeUserIds = collectLeaderboardUserIds({
      debriefs,
      comments,
      reactions,
      quizEntries: normalizedQuizEntries
    });
    const knownProfileIds = new Set(profileSeed.map((profile) => profile.id).filter(Boolean));
    const missingProfileIds = activeUserIds.filter((id) => !knownProfileIds.has(id));
    const activeProfiles = await fetchProfilesByIds(missingProfileIds);
    const profiles = mergeProfilesById(profileSeed, activeProfiles);

    const filterUserIds = scope === 'friends' ? [...friendIds] : null;
    const filterCompetition = scope === 'competition' ? competitionId || competitions[0]?.id || '' : null;
    const rows = buildLeaderboard({
      profiles,
      debriefs,
      comments,
      reactions,
      quizEntries: normalizedQuizEntries,
      filterUserIds,
      competitionId: filterCompetition || null
    });
    const scopeLabel = scope === 'friends' ? tr('Amis', 'Friends') : scope === 'competition' ? tr('Compétition', 'Competition') : 'Global';
    const selectedCompetition = competitions.find((competition) => competition.id === filterCompetition);
    const scoreShareData = {
      rows,
      scopeLabel,
      competitionName: scope === 'competition' ? selectedCompetition?.name || '' : ''
    };

    content.innerHTML = `
      <div class="flex-col gap-16">
        <div class="glass-card" style="padding:14px;">
          <div class="actions" style="margin-bottom:12px;">
            ${scopeButton('global', 'Global', scope)}
            ${scopeButton('friends', tr('Amis', 'Friends'), scope)}
            ${scopeButton('competition', tr('Compétition', 'Competition'), scope)}
          </div>
          ${scope === 'competition' ? renderCompetitionSelect(competitions, filterCompetition) : ''}
          ${scope === 'friends' && !user ? `<p class="text-muted" style="margin:8px 0 0;">${tr('Connecte-toi pour voir ton classement entre amis.', 'Sign in to view your friends ranking.')}</p>` : ''}
        </div>
        ${renderScoreSocialExport()}
        ${renderRows(rows)}
      </div>
    `;

    content.querySelectorAll('[data-scope]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const next = btn.dataset.scope;
        window.location.hash = `#/leaderboards?scope=${next}${next === 'competition' && filterCompetition ? `&competition=${filterCompetition}` : ''}`;
      });
    });
    content.querySelector('[data-competition-select]')?.addEventListener('change', (e) => {
      window.location.hash = `#/leaderboards?scope=competition&competition=${e.target.value}`;
    });

    const socialSelect = content.querySelector('#score-social-platform');
    const socialPreview = content.querySelector('#score-social-preview');
    content.querySelector('#btn-score-social-copy')?.addEventListener('click', async () => {
      const payload = buildScoreSharePayload(scoreShareData, { platform: socialSelect.value, url: window.location.href });
      await navigator.clipboard.writeText(payload.text);
      socialPreview.innerHTML = renderSocialTextPreview(payload);
      showToast(tr('Texte social copié.', 'Social text copied.'));
    });
    content.querySelector('#btn-score-social-open')?.addEventListener('click', () => {
      const payload = buildScoreSharePayload(scoreShareData, { platform: socialSelect.value, url: window.location.href });
      const shareUrl = buildPlatformShareUrl(payload.platform.id, { text: payload.text, url: window.location.href });
      socialPreview.innerHTML = renderSocialTextPreview(payload);
      if (shareUrl) window.open(shareUrl, '_blank', 'noopener,noreferrer');
      else showToast(tr('Plateforme sans intent web : copie le texte et télécharge l’image.', 'Platform has no web intent: copy the text and download the image.'));
    });
    content.querySelector('#btn-score-social-image')?.addEventListener('click', async (event) => {
      const btn = event.currentTarget;
      btn.disabled = true;
      btn.textContent = tr('Génération...', 'Generating...');
      try {
        const payload = buildScoreSharePayload(scoreShareData, { platform: socialSelect.value, url: window.location.href });
        const imageUrl = await generateScoreShareImage(scoreShareData, { platform: socialSelect.value, url: window.location.href });
        socialPreview.innerHTML = `
          ${renderSocialTextPreview(payload)}
          <img src="${imageUrl}" alt="${tr('Aperçu social scores', 'Score social preview')}" style="width:100%;max-height:520px;object-fit:contain;border:1px solid var(--line);border-radius:8px;margin-top:10px;background:var(--surface-2);" />
          <button id="btn-download-score-social" class="btn-primary" type="button" style="width:100%;margin-top:10px;">${tr('Télécharger le PNG', 'Download PNG')}</button>
        `;
        socialPreview.querySelector('#btn-download-score-social').addEventListener('click', () => downloadDataUrl(imageUrl, `fooview-scores-${payload.platform.id}.png`));
      } catch (err) {
        showToast(tr('Image sociale impossible: ', 'Social image failed: ') + err.message);
      } finally {
        btn.disabled = false;
        btn.textContent = tr('Image', 'Image');
      }
    });
  } catch {
    content.innerHTML = `
      <div class="glass-card">
        <p style="font-weight:700;margin-bottom:8px;">${tr('Classements indisponibles', 'Rankings unavailable')}</p>
        <p class="text-muted" style="margin:0;">${tr('Applique les migrations gamification pour activer cette page.', 'Apply the gamification migrations to enable this page.')}</p>
      </div>`;
  }
}

async function safeQuery(factory) {
  const { data, error } = await factory();
  if (error) return [];
  return data || [];
}

async function fetchProfilesByIds(ids) {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (!uniqueIds.length) return [];
  const chunks = [];
  for (let i = 0; i < uniqueIds.length; i += 100) {
    chunks.push(uniqueIds.slice(i, i + 100));
  }
  const results = await Promise.all(chunks.map((chunk) => safeQuery(() =>
    supabase
      .from('profiles')
      .select('id,pseudo,avatar_url,avatar_emoji')
      .in('id', chunk)
  )));
  return results.flat();
}

function scopeButton(id, label, current) {
  return `<button class="${current === id ? 'btn-primary' : 'btn-secondary'}" data-scope="${id}" type="button">${label}</button>`;
}

function renderCompetitionSelect(competitions, selected) {
  if (!competitions.length) return `<p class="text-muted" style="margin:0;">${tr('Aucune compétition configurée.', 'No competition configured.')}</p>`;
  return `
    <select class="input-glow" data-competition-select style="width:100%;">
      ${competitions.map(c => `<option value="${escapeHtml(c.id)}" ${c.id === selected ? 'selected' : ''}>${escapeHtml(c.name)}</option>`).join('')}
    </select>
  `;
}

function renderRows(rows) {
  if (!rows.length) {
    return `<div class="glass-card"><p class="text-muted" style="margin:0;">${tr('Pas encore assez d’activité pour classer les joueurs.', 'Not enough activity to rank players yet.')}</p></div>`;
  }
  return `
    <div class="flex-col gap-12">
      ${rows.slice(0, 50).map((row, index) => `
        <article class="glass-card" style="padding:14px;display:grid;grid-template-columns:44px auto minmax(0,1fr) auto;gap:12px;align-items:center;">
          <div style="font-weight:900;font-size:20px;color:var(--primary);">#${index + 1}</div>
          <div class="avatar">${renderAvatarInner(row, { imgStyle: '' })}</div>
          <div style="min-width:0;">
            <p style="font-weight:850;margin:0;">${escapeHtml(row.pseudo)}</p>
            <p class="text-muted" style="font-size:12px;margin:4px 0 0;">${rankLabel(index)} · ${tr('Niv.', 'Lvl.')} ${row.level} · ${row.debriefs} ${tr('analyses', 'analyses')} · ${row.quizzes} quiz</p>
          </div>
          <strong style="font-size:18px;">${row.points} pts</strong>
        </article>
      `).join('')}
    </div>
  `;
}

function renderScoreSocialExport() {
  return `
    <div class="glass-card" id="score-social-export-card">
      <p style="font-weight:700;margin:0 0 10px;">${tr('Partager les scores', 'Share scores')}</p>
      <div class="actions">
        <select id="score-social-platform" class="input-glow" style="flex:1;min-width:180px;">
          ${listSocialPlatforms().map(p => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.label)}</option>`).join('')}
        </select>
        <button id="btn-score-social-image" class="btn-secondary" type="button">Image</button>
        <button id="btn-score-social-copy" class="btn-secondary" type="button">${tr('Texte', 'Text')}</button>
        <button id="btn-score-social-open" class="btn-secondary" type="button">${tr('Poster', 'Post')}</button>
      </div>
      <div id="score-social-preview" style="margin-top:12px;"></div>
    </div>
  `;
}

function renderSocialTextPreview(payload) {
  return `
    <div style="padding:10px;border:1px solid var(--line);border-radius:8px;background:var(--surface-2);">
      <p class="text-muted" style="font-size:12px;margin:0 0 6px;">${escapeHtml(payload.platform.label)} · ${escapeHtml(payload.format.label)} ${payload.format.width}x${payload.format.height}</p>
      <p style="white-space:pre-wrap;margin:0;font-size:13px;line-height:1.5;">${escapeHtml(payload.text)}</p>
    </div>
  `;
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function showToast(msg) {
  const t = document.createElement('div');
  t.className = 'toast show';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => { t.className = 'toast'; setTimeout(() => t.remove(), 300); }, 3000);
}
